import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import linkNotSentImage from 'assets/svg/link-not-sent.png';
import Loading from 'components/Loading';
import { useAppDispatch } from 'hooks/hooks';
import { useFilterInspectionById } from 'hooks/Inspections/InspectionsHooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import InspectionStatusService from 'services/ApiService/InspectionStatusService/InspectionStatusService';
import { setPresentInspection } from 'stores/presentInspection/presentInspection.store';
import { InspectionCategoryEnum } from 'utils/InspectionEnum';
import { IInspection, StatusEnum } from '../Inspection/Inspection.types';
import EmptyInspection from './components/EmptyInspection/EmptyInspection';
import InspectionDetailsRouter from './InspectionDetails.router';
import * as S from './inspectionDetails.styles';
import { InspectionDetailsProps } from './InspectionDetails.types';

const MESSAGES = {
  SUCCESS: 'Status atualizado com sucesso!',
  ERROR: 'Erro ao atualizar o status. Tente novamente.',
  ERROR_LOG: 'Erro ao confirmar envio do link:',
} as const;

const QUERY_KEYS = {
  INSPECTION_BY_ID: 'get-inspection-by-id',
  INSPECTIONS: 'get-inspections',
} as const;

const InspectionDetails = ({ inspectionId }: InspectionDetailsProps) => {
  const { inspectionData, isLoadingInspection } = useFilterInspectionById(inspectionId, 'DETAILS');
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const currentStatusTypeIsDefault = (inspection: IInspection): boolean => {
    return InspectionStatusService.isCurrentStatus(inspection.currentStatus, StatusEnum.DEFAULT);
  };

  const isInspectionCategoryDigital = (): boolean => {
    return inspectionData?.inspectionCategory?.code === InspectionCategoryEnum.DIGITAL;
  };

  const updatePresentInspection = useCallback(() => {
    if (!inspectionData) return;
    dispatch(setPresentInspection(inspectionData));
  }, [dispatch, inspectionData]);

  const handleConfirmLinkSent = async () => {
    if (!inspectionData) return;

    try {
      setIsConfirmLoading(true);
      await InspectionStatusService.skipLinkSent(inspectionData.id);
      toast.success(MESSAGES.SUCCESS);

      queryClient.invalidateQueries([QUERY_KEYS.INSPECTION_BY_ID, { id: inspectionData.id }]);
      queryClient.invalidateQueries([QUERY_KEYS.INSPECTIONS]);
    } catch (error: unknown) {
      console.error(MESSAGES.ERROR_LOG, error);
      toast.error(MESSAGES.ERROR);
    } finally {
      setIsConfirmLoading(false);
    }
  };

  useEffect(() => {
    updatePresentInspection();
  }, [updatePresentInspection]);

  if (inspectionData && currentStatusTypeIsDefault(inspectionData) && isInspectionCategoryDigital()) {
    return (
      <S.ContainerLinkNotSent data-testid="link-not-sent">
        <img src={linkNotSentImage} alt="link-not-sent" />

        <S.TitleLinkNotSent>Link não enviado</S.TitleLinkNotSent>
        <S.DescriptionLinkNotSent>
          Houve um problema com o envio do link. Tente enviar novamente clicando no botão
          <span> Reenviar Link.</span>
        </S.DescriptionLinkNotSent>

        <S.DescriptionLinkNotSent>
          Caso tenha sido enviado, clique no botão abaixo para pular o envio.
        </S.DescriptionLinkNotSent>
        <S.ButtonLink
          variant="contained"
          type="button"
          fullWidth={false}
          size="small"
          startIcon={<CheckCircleIcon />}
          testID="open-resend-link-dialog"
          text="CONFIRMAR QUE FOI ENVIADO"
          onClick={handleConfirmLinkSent}
          loading={isConfirmLoading}
          loadingSize={25}
          loadingColor="#FFF"
          disabled={isConfirmLoading}
        />
      </S.ContainerLinkNotSent>
    );
  }

  const showInspectionDetails = () => {
    if (isLoadingInspection) {
      return (
        <S.DetailsLoadingWrapper>
          <Loading width="20%" />
        </S.DetailsLoadingWrapper>
      );
    }

    return (
      <S.Container>
        {inspectionData ? <InspectionDetailsRouter inspection={inspectionData} /> : <EmptyInspection />}
      </S.Container>
    );
  };

  return <>{showInspectionDetails()}</>;
};

export default InspectionDetails;
